import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ModelGender, ShowModelSummary, ShowSummary, ShowType } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { resolveApiUrl, resolveAppUrl } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Link } from 'react-router-dom';

import { useAuth, useAuthModal } from '../auth/index.js';
import { useShowRealtime } from '../shows/useShowRealtime.js';
import { GenderIcon } from '../../shared/components/GenderIcon/index.js';
import { camBadgeClass, camBadgeLabel } from './cam-show-badge.js';
import { GRID_PAGE_SIZE, ThumbGridPager } from '../../shared/components/ThumbGridPager/index.js';
import { camThumbUrl } from '../../shared/placeholder-media.js';
import '../../shared/styles/thumb-grid.css';
import './HomePage.css';
import './CamsBrowsePage.css';

const studioUrl = resolveAppUrl('studio', import.meta.env);
const apiBaseUrl = resolveApiUrl(import.meta.env);

type FavoriteMap = ReadonlyMap<string, number>;

const CAM_GENDER_FILTERS = ['WOMAN', 'MAN', 'TRANS', 'COUPLE'] as const satisfies readonly ModelGender[];
type CamGenderFilter = (typeof CAM_GENDER_FILTERS)[number];

const GENDER_FILTER_LABELS: Record<CamGenderFilter, string> = {
  WOMAN: 'common.home.genderWoman',
  MAN: 'common.home.genderMan',
  TRANS: 'common.home.genderTrans',
  COUPLE: 'common.home.genderCouple',
};

const GENDER_FILTER_LETTERS: Record<CamGenderFilter, string> = {
  WOMAN: 'common.home.genderWomanLetter',
  MAN: 'common.home.genderManLetter',
  TRANS: 'common.home.genderTransLetter',
  COUPLE: 'common.home.genderCoupleLetter',
};

function camCardClass(type: ShowType): string {
  return `thumb-card ${camBadgeClass(type).replace('thumb-card__badge--', 'thumb-card--')}`;
}

interface CamFavoriteButtonProps {
  username: string;
  displayName: string;
  isFavorite: boolean;
  favoriteRank: number | null;
  busy: boolean;
  onToggle: (username: string, isFavorite: boolean) => void;
}

function CamFavoriteButton({
  username,
  displayName,
  isFavorite,
  favoriteRank,
  busy,
  onToggle,
}: CamFavoriteButtonProps): ReactElement {
  const label = isFavorite
    ? t('common.home.faveRemove').replace('{{name}}', displayName)
    : t('common.home.faveAdd').replace('{{name}}', displayName);

  return (
    <button
      type="button"
      className={
        isFavorite ? 'thumb-card__fave thumb-card__fave--active' : 'thumb-card__fave'
      }
      aria-label={label}
      aria-pressed={isFavorite}
      disabled={busy}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle(username, isFavorite);
      }}
    >
      {isFavorite && favoriteRank !== null ? (
        <span className="thumb-card__fave-rank">#{favoriteRank}</span>
      ) : (
        <span aria-hidden="true">{isFavorite ? '★' : '☆'}</span>
      )}
    </button>
  );
}

function CamShowCard({
  show,
  isFavorite,
  favoriteRank,
  busy,
  onToggleFavorite,
}: {
  show: ShowSummary;
  isFavorite: boolean;
  favoriteRank: number | null;
  busy: boolean;
  onToggleFavorite: (username: string, isFavorite: boolean) => void;
}): ReactElement {
  const onBreak = show.status === 'PAUSED';
  const isAway = show.status === 'AWAY';
  return (
    <article className={camCardClass(show.type)}>
      <div className="thumb-card__thumb">
        <Link
          to={`/@${show.model.username}`}
          className="thumb-card__hit"
          aria-label={show.model.displayName}
        >
          <img
            src={camThumbUrl(show.model.username)}
            alt=""
            loading="lazy"
            width={640}
            height={360}
          />
          <span className={`thumb-card__badge ${camBadgeClass(show.type)}`}>
            {isAway
              ? t('common.show.away')
              : onBreak
                ? t('common.show.onBreak')
                : camBadgeLabel(show.type)}
          </span>
          <div className="thumb-card__meta">
            <div className="thumb-card__title-row">
              <GenderIcon gender={show.model.gender} />
              <h3 className="thumb-card__title">{show.model.displayName}</h3>
            </div>
          </div>
        </Link>
        <CamFavoriteButton
          username={show.model.username}
          displayName={show.model.displayName}
          isFavorite={isFavorite}
          favoriteRank={favoriteRank}
          busy={busy}
          onToggle={onToggleFavorite}
        />
      </div>
    </article>
  );
}

function OfflineCamCard({
  model,
  isFavorite,
  favoriteRank,
  busy,
  onToggleFavorite,
}: {
  model: ShowModelSummary;
  isFavorite: boolean;
  favoriteRank: number | null;
  busy: boolean;
  onToggleFavorite: (username: string, isFavorite: boolean) => void;
}): ReactElement {
  return (
    <article className="thumb-card thumb-card--offline">
      <div className="thumb-card__thumb thumb-card__thumb--offline">
        <Link
          to={`/@${model.username}`}
          className="thumb-card__hit"
          aria-label={model.displayName}
        >
          <img src={camThumbUrl(model.username)} alt="" loading="lazy" width={640} height={360} />
          <div className="thumb-card__meta">
            <div className="thumb-card__title-row">
              <GenderIcon gender={model.gender} />
              <h3 className="thumb-card__title">{model.displayName}</h3>
            </div>
          </div>
        </Link>
        <CamFavoriteButton
          username={model.username}
          displayName={model.displayName}
          isFavorite={isFavorite}
          favoriteRank={favoriteRank}
          busy={busy}
          onToggle={onToggleFavorite}
        />
      </div>
    </article>
  );
}

export function CamsBrowsePage(): ReactElement {
  const { api, user } = useAuth();
  const { requireAuth } = useAuthModal();
  const [online, setOnline] = useState<ShowSummary[]>([]);
  const [offline, setOffline] = useState<ShowModelSummary[]>([]);
  const [onlineTotal, setOnlineTotal] = useState(0);
  const [offlineTotal, setOfflineTotal] = useState(0);
  const [onlinePage, setOnlinePage] = useState(1);
  const [offlinePage, setOfflinePage] = useState(1);
  const [favoritesByUsername, setFavoritesByUsername] = useState<FavoriteMap>(() => new Map());
  const [busyUsername, setBusyUsername] = useState<string | null>(null);
  const [faveError, setFaveError] = useState<string | null>(null);
  const [selectedGenders, setSelectedGenders] = useState<CamGenderFilter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshFavorites = useCallback(() => {
    if (!user) {
      setFavoritesByUsername(new Map());
      return;
    }

    void api
      .listFavorites()
      .then((result) => {
        setFavoritesByUsername(
          new Map(result.favorites.map((favorite) => [favorite.username, favorite.rank])),
        );
      })
      .catch(() => {
        // Favorites are optional UI chrome; cams still load.
      });
  }, [api, user]);

  const refreshCams = useCallback(
    (options?: { initial?: boolean }) => {
      void api
        .listCams(selectedGenders.length > 0 ? selectedGenders : undefined, {
          onlinePage,
          offlinePage,
          limit: GRID_PAGE_SIZE,
        })
        .then((result) => {
          setOnline(result.online);
          setOffline(result.offline);
          setOnlineTotal(result.onlineTotal ?? result.online.length);
          setOfflineTotal(result.offlineTotal ?? result.offline.length);
          setError(null);
        })
        .catch(() => {
          setError('Unable to load cams.');
        })
        .finally(() => {
          if (options?.initial) {
            setIsLoading(false);
          }
        });
    },
    [api, selectedGenders, onlinePage, offlinePage],
  );

  const toggleGenderFilter = (filter: CamGenderFilter): void => {
    setOnlinePage(1);
    setOfflinePage(1);
    setSelectedGenders((current) =>
      current.includes(filter)
        ? current.filter((value) => value !== filter)
        : [...current, filter],
    );
  };

  useEffect(() => {
    setIsLoading(true);
    refreshCams({ initial: true });
  }, [refreshCams]);

  useEffect(() => {
    refreshFavorites();
  }, [refreshFavorites]);

  useShowRealtime(
    apiBaseUrl,
    { cams: true },
    {
      onCamsChanged: () => {
        refreshCams();
      },
    },
  );

  const handleToggleFavorite = (username: string, isFavorite: boolean): void => {
    requireAuth(() => {
      setBusyUsername(username);
      setFaveError(null);
      const next = isFavorite ? api.unfavoriteModel(username) : api.favoriteModel(username);

      void next
        .then((result) => {
          setFavoritesByUsername(
            new Map(result.favorites.map((favorite) => [favorite.username, favorite.rank])),
          );
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.code === 'FAVORITE_LIMIT_REACHED') {
            setFaveError(t('common.home.faveLimit'));
            return;
          }
          setFaveError(err instanceof ApiError ? err.message : t('common.storefront.socialError'));
        })
        .finally(() => {
          setBusyUsername(null);
        });
    }, 'follow');
  };

  return (
    <section className="browse-page">
      <div className="browse-page__content" id="live">
        <div className="fm-section-head home-section-head">
          <h2 className="fm-section-head__title">{t('common.home.liveTitle')}</h2>
          <span className="fm-section-head__meta">{onlineTotal}</span>
        </div>
        <div className="browse-page__filters" role="group" aria-label={t('common.home.genderFilter')}>
          {CAM_GENDER_FILTERS.map((filter) => {
            const checked = selectedGenders.includes(filter);
            return (
              <label
                key={filter}
                className={
                  checked
                    ? 'browse-page__filter browse-page__filter--active'
                    : 'browse-page__filter'
                }
              >
                <input
                  type="checkbox"
                  className="browse-page__filter-checkbox"
                  checked={checked}
                  onChange={() => {
                    toggleGenderFilter(filter);
                  }}
                />
                <GenderIcon gender={filter} />
                <span className="browse-page__filter-letter" aria-hidden>
                  {t(GENDER_FILTER_LETTERS[filter])}
                </span>
                <span className="browse-page__filter-label">{t(GENDER_FILTER_LABELS[filter])}</span>
              </label>
            );
          })}
        </div>

        {isLoading && <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.auth.loading')}</p>}
        {error && <p className="fm-error">{error}</p>}
        {faveError ? <p className="fm-error">{faveError}</p> : null}

        {!isLoading && onlineTotal === 0 ? (
          <div className="home-empty-live" role="status">
            <div className="home-empty-live__copy">
              <p className="home-empty-live__title">{t('common.home.emptyLiveTitle')}</p>
              <p className="home-empty-live__body">{t('common.home.emptyLiveBody')}</p>
            </div>
            <a className="home-empty-live__cta" href={studioUrl}>
              {t('common.footer.broadcast')}
            </a>
          </div>
        ) : null}

        {online.length > 0 ? (
          <>
            <ul className="thumb-grid">
              {online.map((show) => {
                const rank = favoritesByUsername.get(show.model.username) ?? null;
                return (
                  <li key={show.id}>
                    <CamShowCard
                      show={show}
                      isFavorite={rank !== null}
                      favoriteRank={rank}
                      busy={busyUsername === show.model.username}
                      onToggleFavorite={handleToggleFavorite}
                    />
                  </li>
                );
              })}
            </ul>
            <ThumbGridPager
              page={onlinePage}
              total={onlineTotal}
              pageSize={GRID_PAGE_SIZE}
              label={t('common.home.liveTitle')}
              onPageChange={setOnlinePage}
            />
          </>
        ) : null}

        <div className="fm-section-head home-section-head home-section-head--offline">
          <h2 className="fm-section-head__title">{t('common.home.offlineTitle')}</h2>
          <span className="fm-section-head__meta">{offlineTotal}</span>
        </div>

        {offline.length > 0 ? (
          <>
            <ul className="thumb-grid">
              {offline.map((model) => {
                const rank = favoritesByUsername.get(model.username) ?? null;
                return (
                  <li key={model.id}>
                    <OfflineCamCard
                      model={model}
                      isFavorite={rank !== null}
                      favoriteRank={rank}
                      busy={busyUsername === model.username}
                      onToggleFavorite={handleToggleFavorite}
                    />
                  </li>
                );
              })}
            </ul>
            <ThumbGridPager
              page={offlinePage}
              total={offlineTotal}
              pageSize={GRID_PAGE_SIZE}
              label={t('common.home.offlineTitle')}
              onPageChange={setOfflinePage}
            />
          </>
        ) : null}
      </div>
    </section>
  );
}
